<template>
	<div class="home-block">
		<div class="d-flex align-items-center mb-3">
			<font-awesome-icon :icon="['fal', 'file-invoice']" class="mr-2 icon-groomy-red" />
			<h3 class="mb-0 mr-auto">{{ $t('invoice.mes_factures') }}</h3>
			<b-button v-if="hasPendingBalance" variant="primary" size="sm" @click="payBalance">
				{{ $t('invoice.solde_a_payer') }}
				<span v-for="(formatted, curr) in balanceByCurrency" :key="curr" class="ml-1">{{ formatted }}</span>
				<font-awesome-icon :icon="['fal', 'credit-card']" class="ml-1" />
			</b-button>
		</div>

		<div v-if="busy" class="text-center py-3">
			<LoadingSpinner />
		</div>
		<EmptyState v-else-if="invoices.length === 0" :label="$t('invoice.no_facture_to_pay')" />
		<div v-else>
			<!-- En-tête desktop -->
			<div class="d-none d-lg-flex row font-weight-bold border-bottom pb-1 mb-1 small text-muted">
				<div class="col-lg-4">{{ $t('table.invoices.invoice_num') }}</div>
				<div class="col-lg-4">{{ $t('table.invoices.invoice_date') }}</div>
				<div class="col-lg-4 text-right">{{ $t('invoice.a_regler') }}</div>
			</div>
			<!-- Lignes -->
			<div v-for="inv in invoices" :key="inv.invoice_id" class="border-bottom py-2">
				<!-- Desktop -->
				<div class="d-none d-lg-flex row align-items-center small" style="min-width: 0">
					<div class="col-lg-4 text-muted text-truncate">#{{ inv.invoice_num }}</div>
					<div class="col-lg-4 text-truncate">{{ inv.invoice_date_formatted }}</div>
					<div class="col-lg-4 text-right text-truncate" :class="inv.duedate_balance > 0 ? 'text-danger font-weight-bold' : 'text-muted'">
						{{ inv.duedate_balance > 0 ? inv.balance_formatted : '—' }}
					</div>
				</div>
				<!-- Mobile -->
				<div class="d-lg-none" style="min-width: 0">
					<div class="d-flex align-items-center justify-content-between">
						<span class="font-weight-bold text-truncate mr-2">{{ inv.tiers_rs }}</span>
						<span class="flex-shrink-0" :class="inv.duedate_balance > 0 ? 'text-danger font-weight-bold' : 'text-muted'">
							{{ inv.duedate_balance > 0 ? inv.balance_formatted : '—' }}
						</span>
					</div>
					<small class="text-muted text-truncate d-block">#{{ inv.invoice_num }} · {{ inv.invoice_date_formatted }}</small>
				</div>
			</div>
		</div>

		<div class="text-center mt-3">
			<b-button variant="secondary" :to="{ name: 'addressedInvoices' }" tag="router-link">
				{{ $t('invoice.voir_toutes_factures') }}
				<b-badge v-if="total > 0" variant="primary" class="ml-1">{{ total }}</b-badge>
			</b-button>
		</div>
	</div>
</template>

<script>
import InvoiceMixin from '@/mixins/Invoice.js'
import Tools from 'GroomyRoot/mixins/Tools.js'
import Common from 'GroomyRoot/assets/js/common.js'

export default {
	name: 'HomeInvoices',
	mixins: [InvoiceMixin, Tools],
	data() {
		return {
			all_invoices: [],
			busy: false,
			balanceByCurrency: {},  // { EUR: '450,00 €', CHF: '300,00 CHF' }
		}
	},
	computed: {
		/** Vrai si au moins une devise a un solde impayé */
		hasPendingBalance() {
			return Object.keys(this.balanceByCurrency).length > 0
		},
		/** 8 factures les plus récentes (par date desc) */
		invoices() {
			return this.all_invoices.slice().sort((a, b) =>
				new Date(b.invoice_date) - new Date(a.invoice_date)
			).slice(0, 8)
		},
		total() {
			return this.all_invoices.length
		},
	},
	async mounted() {
		this.busy = true
		try {
			const [toPay, sold] = await Promise.all([
				this.getAddressedInvoicesToPay(),
				this.getAddressedInvoicesSold(),
			])
			const raw = [...(toPay || []), ...(sold || [])]

			// Dédupliquer par invoice_id (une facture peut avoir plusieurs échéances)
			// Chaque item est un duedate ; les données de la facture sont dans item.invoice
			const byId = new Map()
			for (const item of raw) {
				const id = item.invoice ? item.invoice.invoice_id : item.duedate_invoice
				if (!byId.has(id) || (item.duedate_balance > 0 && byId.get(id).duedate_balance <= 0)) {
					byId.set(id, item)
				}
			}

			// Calculer le solde par devise (duedate_balance est déjà divisé par 100 côté API).
			// Cumul en centimes entiers : additionner les euros laisserait un résidu de virgule
			// que priceFormat tronquerait à un centime près.
			const currencyTotals = {}
			for (const item of (toPay || [])) {
				if (item.duedate_balance > 0) {
					const inv = item.invoice || {}
					const currency = inv.author ? (inv.author.tiers_currency || 'EUR') : 'EUR'
					currencyTotals[currency] = (currencyTotals[currency] || 0) + Math.round(item.duedate_balance * 100)
				}
			}
			const byCurrency = {}
			for (const [currency, cents] of Object.entries(currencyTotals)) {
				byCurrency[currency] = await Common.priceFormat(cents / 100, currency, true)
			}
			this.balanceByCurrency = byCurrency

			// Formater les lignes (duedate_balance déjà /100 depuis l'API)
			this.all_invoices = await Promise.all([...byId.values()].map(async item => {
				const inv = item.invoice || {}
				const currency = inv.author ? (inv.author.tiers_currency || 'EUR') : 'EUR'
				return {
					invoice_id:             inv.invoice_id,
					invoice_num:            inv.invoice_num,
					invoice_date:           inv.invoice_date,
					invoice_date_formatted: this.formatDate(inv.invoice_date),
					tiers_rs:               inv.tiers ? inv.tiers.tiers_rs : '',
					currency,
					duedate_balance:        item.duedate_balance || 0,
					balance_formatted:      item.duedate_balance > 0 ? await Common.priceFormat(item.duedate_balance, currency, true) : '',
				}
			}))
		} finally {
			this.busy = false
		}
	},
	methods: {
		/** Ouvre la page de paiement externe de la licence s'il y en a une, sinon la liste des factures */
		payBalance() {
			const external_url = this.getExternalPaymentUrl()
			if(external_url) {
				window.open(external_url, '_blank')
				return
			}
			this.$router.push({ name: 'addressedInvoices' })
		},
	},
	components: {
		LoadingSpinner: () => import('GroomyRoot/components/Logos/LoadingSpinner_35'),
		EmptyState:     () => import('@/components/EmptyState'),
	},
}
</script>
